import { useEffect, useRef, useState } from 'react';
import { Box, Button, Tab, Tabs, Typography, useTheme } from '@mui/material';
import reportTiles from '../../../../assets/img/reports/class-report-tiles.svg';
import { getReports as getDistrictReports } from '../../../services/reports/DistrictReportService';
import { getReports as getSchoolReports } from '../../../services/reports/SchoolReportService';
import { getReports as getSubscriptionReports } from '../../../services/reports/SubscriptionReportService';
import { getReportsForClassMode } from '../../../services/reports/ClassReportService';
import type { ReportDefinition } from '../../../types/ReportDefinition';
import { getReports } from '../../../services/reports/CurriculumReportService';
import { getSavedCollectionIds, saveCollectionIds } from '../../../services/reports/ReportCollectionFilterService';
import { ALL_OPTION_ID, SelectComponent } from '../../../components/core/SelectComponent/SelectComponent';
import { getUserPermissions, hasPermission, PermissionConsts } from '../../../services/PermissionsService';
import { toOptionalPositiveInteger } from '../../../services/Utilities';
import { SubscriptionFeature } from '../../../components/core/SubscriptionFeature/SubscriptionFeature';
import { reportPickerStyles } from './ReportPicker.styles';

type ReportCategory = 'general' | 'collection';

interface ReportPickerProps {
  userId?: number | string;
  isCurriculumMode?: boolean;
  isClassMode?: boolean;
  isSchoolAdminMode?: boolean;
  isDistrictAdminMode?: boolean;
  isSubscriptionMode?: boolean;
  districtId?: number | string;
  schoolId?: number | string;
  subscriptionId?: number | string;
  classId?: number | string;
  onReportSelect?: (reportId: string, reportSubscriptionId?: number | string) => void;
}

export const ReportPicker = ({
  userId,
  isCurriculumMode = false,
  isClassMode = false,
  isSchoolAdminMode = false,
  isDistrictAdminMode = false,
  isSubscriptionMode = false,
  districtId,
  classId,
  schoolId,
  subscriptionId,
  onReportSelect,
}: ReportPickerProps = {}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<ReportCategory>('general');
  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [requestState, setRequestState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [accessDecision, setAccessDecision] = useState<{ userId: number; allowed: boolean } | null>(null);
  const [savedCollectionIds, setSavedCollectionIds] = useState<string[]>([]);
  const [filterReady, setFilterReady] = useState(false);
  const [filterSaving, setFilterSaving] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);
  // Pending preference reads and writes must not update the next user's filter.
  const filterVersion = useRef(0);
  const normalizedUserId = toOptionalPositiveInteger(userId);
  const normalizedClassId = toOptionalPositiveInteger(classId);
  const normalizedSchoolId = toOptionalPositiveInteger(schoolId);
  const normalizedDistrictId = toOptionalPositiveInteger(districtId);
  const normalizedSubscriptionId = toOptionalPositiveInteger(subscriptionId);
  const curriculumMode = isCurriculumMode && !isClassMode;
  const schoolMode = isSchoolAdminMode && !isClassMode && !curriculumMode;
  const districtMode = isDistrictAdminMode && !isClassMode && !curriculumMode && !schoolMode;
  const adminMode = schoolMode || districtMode;
  const subscriptionMode = isSubscriptionMode && !isClassMode && !curriculumMode && !adminMode;
  const isReportMode = isClassMode || curriculumMode || adminMode || subscriptionMode;
  const hasCollectionFilter = adminMode || subscriptionMode;
  // Hide the previous user's reports immediately, before the effect checks the new user's permissions.
  const canViewReports = accessDecision && accessDecision.userId === normalizedUserId ? accessDecision.allowed : null;

  useEffect(() => {
    const version = ++filterVersion.current;
    setSavedCollectionIds([]);
    setFilterReady(false);
    setFilterSaving(false);
    setFilterError(null);
    if (hasCollectionFilter && normalizedUserId) {
      getSavedCollectionIds(normalizedUserId).then((ids) => {
        if (filterVersion.current === version) {
          setSavedCollectionIds(ids);
          setFilterReady(true);
        }
      }).catch(() => {
        if (filterVersion.current === version) {
          setFilterError('Unable to load the saved collection filter.');
        }
      });
    }
    return () => {
      filterVersion.current++;
    };
  }, [hasCollectionFilter, normalizedUserId]);

  useEffect(() => {
    setReports([]);
    setAccessDecision(null);
    if (!isReportMode) {
      setRequestState('idle');
      return;
    }

    let cancelled = false;
    setRequestState('loading');
    async function loadReports(): Promise<ReportDefinition[]> {
      if (isClassMode) {
        if (!normalizedClassId) {
          throw new Error('A valid class is required');
        }
        return getReportsForClassMode(normalizedClassId);
      }

      if (schoolMode) {
        if (!normalizedSchoolId || !normalizedSubscriptionId) {
          throw new Error('A valid school and subscription are required');
        }
        return getSchoolReports(normalizedSubscriptionId, normalizedSchoolId);
      }

      if (districtMode) {
        if (!normalizedDistrictId || !normalizedSubscriptionId) {
          throw new Error('A valid district and subscription are required');
        }
        return getDistrictReports(normalizedSubscriptionId, normalizedDistrictId);
      }

      if (subscriptionMode) {
        if (!normalizedSubscriptionId) {
          throw new Error('A valid subscription is required');
        }
        return getSubscriptionReports(normalizedSubscriptionId);
      }

      if (!normalizedUserId) {
        throw new Error('A valid user is required');
      }
      const permissions = await getUserPermissions(normalizedUserId);
      if (cancelled) {
        return [];
      }

      const allowed = hasPermission(permissions, PermissionConsts.ui_curriculum_hide_reports_placeholder);
      setAccessDecision({ userId: normalizedUserId, allowed });
      if (!allowed) {
        return [];
      }
      return getReports();
    }

    loadReports().then((loadedReports) => {
      if (!cancelled) {
        setReports(loadedReports);
        setRequestState('loaded');
      }
    }).catch(() => {
      if (!cancelled) {
        setRequestState('error');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isReportMode, isClassMode, schoolMode, districtMode, subscriptionMode, normalizedClassId, normalizedUserId, normalizedSchoolId, normalizedDistrictId, normalizedSubscriptionId]);

  if (curriculumMode && canViewReports === false) {
    return (
      <Box component="section" aria-label="Report picker" sx={reportPickerStyles.container(theme)}>
        <SubscriptionFeature feature="curriculum_reports_placeholder" />
      </Box>
    );
  }

  if (curriculumMode && canViewReports === null) {
    return (
      <Box component="section" aria-label="Report picker" sx={reportPickerStyles.container(theme)}>
        <Typography role={requestState === 'error' ? 'alert' : 'status'}>
          {requestState === 'error' ? 'Unable to load reports.' : 'Loading reports…'}
        </Typography>
      </Box>
    );
  }

  if (isReportMode && requestState === 'loaded' && reports.length === 0) {
    return (
      <Box component="section" aria-label="Report picker" sx={[reportPickerStyles.container(theme), reportPickerStyles.emptyContainer]}>
        <Box role="status" sx={reportPickerStyles.emptyContent}>
          <Box component="img" src={reportTiles} alt="" sx={reportPickerStyles.emptyImage} />
          <Typography sx={reportPickerStyles.emptyMessage}>Reports are coming soon</Typography>
        </Box>
      </Box>
    );
  }

  const generalReports = reports.filter((report) =>
    // The legacy picker uses this collection ID for reports across all collections.
    report.collections?.some((collection) => collection.collection_uuid === '616C6C')
  );
  const collectionReports = reports.filter((report) =>
    report.collections?.length && !generalReports.includes(report)
  );
  const collectionOptions = Array.from(new Map(collectionReports.flatMap((report) =>
    (report.collections || []).map((collection) => [collection.collection_uuid, collection] as const)
  )).values()).sort((first, second) => (first.sort_order ?? 0) - (second.sort_order ?? 0))
    .map((collection) => ({ id: collection.collection_uuid, name: collection.name || collection.collection_uuid }));
  const selectedCollectionIds = savedCollectionIds.filter((id) => collectionOptions.some((option) => option.id === id));
  const visibleCollectionReports = hasCollectionFilter && selectedCollectionIds.length > 0
    ? collectionReports.filter((report) => report.collections?.some((collection) => selectedCollectionIds.includes(collection.collection_uuid)))
    : collectionReports;

  function updateCollectionFilter(ids: string[]) {
    const nextIds = ids.filter((id) => id !== ALL_OPTION_ID);
    if (!normalizedUserId || !filterReady || filterSaving ||
      (nextIds.length === selectedCollectionIds.length && nextIds.every((id) => selectedCollectionIds.includes(id)))) {
      return;
    }
    const version = filterVersion.current;
    setFilterSaving(true);
    setFilterError(null);
    saveCollectionIds(normalizedUserId, nextIds).then(() => {
      if (filterVersion.current === version) {
        setSavedCollectionIds(nextIds);
      }
    }).catch(() => {
      if (filterVersion.current === version) {
        setFilterError('Unable to save the collection filter. Please try again.');
      }
    }).finally(() => {
      if (filterVersion.current === version) {
        setFilterSaving(false);
      }
    });
  }

  return (
    <Box component="section" aria-label="Report picker" sx={[reportPickerStyles.container(theme), reportPickerStyles.tabbedContainer]}>
      <Tabs
        value={activeTab}
        onChange={(_event, value: ReportCategory) => setActiveTab(value)}
        aria-label="Report categories"
        sx={reportPickerStyles.tabs(theme)}
      >
        <Tab
          value="general"
          label="General Reports"
          id="reports-tab-general"
          aria-controls="reports-panel-general"
          sx={reportPickerStyles.tab(theme)}
        />
        <Tab
          value="collection"
          label="Collection Reports"
          id="reports-tab-collection"
          aria-controls="reports-panel-collection"
          sx={reportPickerStyles.tab(theme)}
        />
      </Tabs>
      {(['general', 'collection'] as const).map((category) => (
        <Box
          key={category}
          role="tabpanel"
          id={`reports-panel-${category}`}
          aria-labelledby={`reports-tab-${category}`}
          hidden={activeTab !== category}
          sx={reportPickerStyles.tabPanel}
        >
          <Typography component="p" sx={reportPickerStyles.description(theme)}>
            {category === 'general'
              ? 'Reports on information that spans course collections.'
              : 'Content-specific reports.'}
          </Typography>
          {isReportMode && requestState === 'loading' && <Typography role="status">Loading reports…</Typography>}
          {isReportMode && requestState === 'error' && <Typography role="alert">Unable to load reports.</Typography>}
          {category === 'collection' && hasCollectionFilter && requestState === 'loaded' && collectionOptions.length > 0 && (
            <Box sx={reportPickerStyles.collectionFilter}>
              <SelectComponent
                label="Collections"
                options={collectionOptions}
                multiple
                allOptionLabel="Collections"
                allOptionId={ALL_OPTION_ID}
                value={selectedCollectionIds.length > 0 ? selectedCollectionIds : [ALL_OPTION_ID]}
                disabled={!filterReady || filterSaving}
                onCloseChange={updateCollectionFilter}
              />
              {filterError && <Typography role="alert">{filterError}</Typography>}
            </Box>
          )}
          {isReportMode && requestState === 'loaded' && (category === 'general' ? generalReports : visibleCollectionReports).length > 0 && (
            <Box component="ul" sx={reportPickerStyles.reportList}>
              {(category === 'general' ? generalReports : visibleCollectionReports).map((report) => (
                <Box component="li" key={adminMode ? `${report.subscription_id ?? normalizedSubscriptionId}:${report.report_id}` : report.report_id}>
                  <Button
                    variant="text"
                    disableFocusRipple
                    onClick={() => {
                      if (adminMode) {
                        onReportSelect?.(String(report.report_id), report.subscription_id ?? normalizedSubscriptionId);
                      } else {
                        onReportSelect?.(String(report.report_id));
                      }
                    }}
                    sx={reportPickerStyles.reportButton(theme)}
                  >
                    {report.thumbnail_url && (
                      <Box component="span" sx={reportPickerStyles.thumbnailFrame}>
                        <Box component="img" src={report.thumbnail_url} alt="" sx={reportPickerStyles.thumbnail} />
                      </Box>
                    )}
                    <Box component="span" className="report-label">{report.name}</Box>
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          {isReportMode && requestState === 'loaded' && (category === 'general' ? generalReports : visibleCollectionReports).length === 0 && (
            <Box role="status" sx={reportPickerStyles.emptyTabContent}>
              <Box component="img" src={reportTiles} alt="" sx={[reportPickerStyles.emptyImage, reportPickerStyles.emptyTabImage]} />
              <Typography sx={[reportPickerStyles.emptyMessage, reportPickerStyles.emptyTabMessage]}>
                You currently don't have any {category === 'general' ? 'General' : 'Collection'} reports
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default ReportPicker;
